import React, { useState, useEffect } from 'react'
import { Box, Chip, Grid, Stack, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { syncApi } from '../../../services/api'
import { poolwerxColors } from '../../../theme/colors'
import BaseDialog from './BaseDialog'
import DialogFooter from './DialogFooter'
import FormErrorSummary from '../../admin/shared/FormErrorSummary'
import { AdminFormSection } from '../../admin/shared/fields'
import { LightspeedIcon, XeroIcon } from '../../SystemIcons'
import { getCountryChipSx } from '../../../utils/countryChips'

export interface BulkSyncDialogProps {
  open: boolean
  onClose: () => void
  /** FP codes to sync. */
  selectedFpCodes: Set<string>
  /** Resolve an FP code to a display name. Defaults to the upper-cased code. */
  getFpDisplayName?: (fpCode: string) => string
  /** Drives the title + count noun: admin syncs all FPs, report syncs the selected report rows. */
  source: 'admin' | 'report'
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

// Available systems for syncing (Skimmer excluded as noted in the codebase).
const SYSTEMS = [
  { label: 'Lightspeed', value: 'lightspeed', icon: <LightspeedIcon sx={{ width: 24, height: 24 }} /> },
  { label: 'Xero', value: 'xero', icon: <XeroIcon sx={{ width: 24, height: 24 }} /> },
]

const BulkSyncDialog: React.FC<BulkSyncDialogProps> = ({
  open,
  onClose,
  selectedFpCodes,
  getFpDisplayName = (fpCode) => fpCode.toUpperCase(),
  source,
  onSuccess,
  onError,
}) => {
  const [selectedSystems, setSelectedSystems] = useState<string[]>(['lightspeed', 'xero'])
  const [syncDialogError, setSyncDialogError] = useState<string | null>(null)
  const [syncOperationInProgress, setSyncOperationInProgress] = useState(false)

  const noun = source === 'admin' ? 'Franchise' : 'FP'
  const title = source === 'admin' ? 'Bulk Sync All Systems' : 'Bulk Sync Selected FPs'

  // Reset state when the dialog opens/closes; ensure all systems are selected on open.
  useEffect(() => {
    setSelectedSystems(['lightspeed', 'xero'])
    if (!open) {
      setSyncDialogError(null)
      setSyncOperationInProgress(false)
    }
  }, [open])

  const toggleSystem = (val: string) =>
    setSelectedSystems((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    )

  const bulkTriggerSync = async () => {
    if (selectedSystems.length === 0) {
      setSyncDialogError('No systems selected for sync')
      return
    }

    try {
      setSyncOperationInProgress(true)
      setSyncDialogError(null)

      const response = await syncApi.bulkTriggerSync({
        fp_codes: Array.from(selectedFpCodes),
        systems: selectedSystems,
        sync_type: 'smart',
        priority: 2,
        confirm_text: 'SYNC',
      })

      const { message, total_syncs_triggered } = response.data

      if (total_syncs_triggered > 0) {
        onSuccess(message)
      } else {
        onError(message)
      }

      onClose()
    } catch (error: any) {
      let errorMessage = 'Unknown error occurred'
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : error.response.data.detail.message || 'Error occurred'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      setSyncDialogError(errorMessage)
      onError(`Failed to trigger bulk sync: ${errorMessage}`)
    } finally {
      setSyncOperationInProgress(false)
    }
  }

  const handleClose = () => {
    if (!syncOperationInProgress) {
      onClose()
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={title}
      variant="sync"
      size="md"
      dismissable={!syncOperationInProgress}
      footer={
        <DialogFooter
          cancel={{ onClick: handleClose, disabled: syncOperationInProgress }}
          primary={{
            label: syncOperationInProgress
              ? 'Triggering Sync...'
              : `Sync ${selectedFpCodes.size} ${noun}${selectedFpCodes.size !== 1 ? 's' : ''}`,
            onClick: bulkTriggerSync,
            pending: syncOperationInProgress,
            disabled: selectedSystems.length === 0,
          }}
        />
      }
    >
      <FormErrorSummary message={syncDialogError} title="Cannot trigger sync" />

      <Grid container spacing={2}>
        <AdminFormSection
          card
          title="Selected Franchises"
          action={
            <Chip
              label={`${selectedFpCodes.size} ${noun}${selectedFpCodes.size !== 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: poolwerxColors.teal, color: 'white', fontWeight: 600 }}
            />
          }
        >
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            <Grid container spacing={1}>
              {Array.from(selectedFpCodes).map((fpCode) => {
                // Market/country lives in the fp_code prefix (au/nz/us).
                const country = fpCode.slice(0, 2).toUpperCase()
                return (
                  <Grid item xs={12} sm={6} key={fpCode}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {getFpDisplayName(fpCode)}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={country}
                        sx={{ ...getCountryChipSx(country), flexShrink: 0 }}
                      />
                    </Box>
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </AdminFormSection>

        <AdminFormSection card title="Systems">
          <Stack spacing={1.5}>
            {SYSTEMS.map((sys) => {
              const selected = selectedSystems.includes(sys.value)
              return (
                <Box
                  key={sys.value}
                  role="button"
                  onClick={() => toggleSystem(sys.value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: selected ? poolwerxColors.teal : 'divider',
                    bgcolor: selected ? poolwerxColors.tealLight : 'transparent',
                    cursor: 'pointer',
                    transition: 'border-color .15s, background-color .15s',
                  }}
                >
                  {sys.icon}
                  <Typography variant="body1" sx={{ fontWeight: 600, flex: 1 }}>
                    {sys.label}
                  </Typography>
                  {selected && <CheckIcon sx={{ color: poolwerxColors.teal }} />}
                </Box>
              )
            })}
          </Stack>
        </AdminFormSection>
      </Grid>
    </BaseDialog>
  )
}

export default BulkSyncDialog
